import React from "react";
import { X } from "lucide-react";
import { Button } from "@shared/ui/button";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * PlaceholderData Type
 *
 * Structure for organizing placeholders by group
 *
 * @example
 * ```
 * const placeholders: PlaceholderData = [
 *   {
 *     group: "CUSTOMER",
 *     items: [
 *       { label: "Customer Name", value: "customer_name" },
 *       { label: "Email", value: "email" }
 *     ]
 *   },
 *   {
 *     group: "BILLING",
 *     items: [
 *       { label: "Bill Amount", value: "bill_amount" }
 *     ]
 *   }
 * ];
 * ```
 */
export type PlaceholderData = Array<{
  group: string;
  items: Array<{
    label: string;
    value: string;
  }>;
}>;

/**
 * PlaceholderModalProps
 *
 * Props interface for PlaceholderModal component
 */
export interface PlaceholderModalProps {
  /** Whether modal is open or closed */
  isOpen: boolean;

  /** Callback to close modal */
  onClose: () => void;

  /** Array of placeholder groups with items */
  placeholders: PlaceholderData;

  /** Callback when placeholder is selected for insertion */
  onInsert: (placeholder: string) => void;

  /** Type of field using this modal (for UI text display) */
  fieldType: "text-with-placeholder" | "textarea-with-placeholder";
}

// ============================================================================
// PLACEHOLDER MODAL COMPONENT
// ============================================================================

/**
 * PlaceholderModal Component
 *
 * A modal dialog for selecting and inserting placeholders into text fields.
 *
 * @description
 * This component displays organized placeholder groups and allows users
 * to click on a placeholder to insert it. The modal can be opened/closed
 * and handles keyboard interactions (Escape key).
 *
 * @features
 * - Organized by groups (CUSTOMER, ACCOUNT, BILLING, etc.)
 * - Click to insert placeholders
 * - Close on escape key press
 * - Click outside to close
 * - Responsive design
 * - Accessibility features
 *
 * @example
 * ```tsx
 * import { PlaceholderModal, PlaceholderData } from "@shared/components/PlaceholderModal";
 *
 * export function MyComponent() {
 *   const [isModalOpen, setIsModalOpen] = useState(false);
 *
 *   const placeholders: PlaceholderData = [
 *     {
 *       group: "CUSTOMER",
 *       items: [
 *         { label: "Customer Name", value: "customer_name" },
 *       ]
 *     }
 *   ];
 *
 *   const handleInsert = (placeholder: string) => {
 *     // placeholder will be "{customer_name}"
 *     console.log("Inserting:", placeholder);
 *   };
 *
 *   return (
 *     <>
 *       <button onClick={() => setIsModalOpen(true)}>
 *         Open Placeholders
 *       </button>
 *
 *       <PlaceholderModal
 *         isOpen={isModalOpen}
 *         onClose={() => setIsModalOpen(false)}
 *         placeholders={placeholders}
 *         onInsert={handleInsert}
 *         fieldType="text-with-placeholder"
 *       />
 *     </>
 *   );
 * }
 * ```
 */
export const PlaceholderModal: React.FC<PlaceholderModalProps> = ({
  isOpen,
  onClose,
  placeholders,
  onInsert,
  fieldType,
}) => {
  // Don't render if modal is closed
  if (!isOpen) return null;

  // Determine title based on field type
  const title =
    fieldType === "text-with-placeholder" ? "Subject" : "Email Body";

  /**
   * Handle escape key press to close modal
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  /**
   * Handle placeholder button click
   * Constructs placeholder string with curly braces and calls onInsert
   */
  const handlePlaceholderClick = (value: string) => {
    onInsert(`{${value}}`);
    onClose();
  };

  return (
    <>
      {/* ================================================================== */}
      {/* Overlay/Backdrop */}
      {/* ================================================================== */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
        role="presentation"
      />

      {/* ================================================================== */}
      {/* Modal Container */}
      {/* ================================================================== */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
        role="presentation"
      >
        {/* Modal Content Box */}
        <div
          className="bg-white rounded-lg shadow-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={handleKeyDown}
          role="dialog"
          aria-labelledby="modal-title"
          aria-modal="true"
        >
          {/* ============================================================ */}
          {/* Modal Header */}
          {/* ============================================================ */}
          <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
            {/* Title */}
            <h2
              id="modal-title"
              className="text-lg font-semibold text-gray-900"
            >
              Insert Placeholder - {title}
            </h2>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors p-1 hover:bg-gray-100 rounded-lg"
              aria-label="Close modal"
              type="button"
              title="Press Escape to close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* ============================================================ */}
          {/* Modal Body */}
          {/* ============================================================ */}
          <div className="p-6 space-y-6">
            {/* Description */}
            <p className="text-sm text-gray-600 leading-relaxed">
              Click any placeholder to insert it into the{" "}
              <strong>{title.toLowerCase()}</strong> field at your cursor
              position.
            </p>

            {/* Placeholder Groups */}
            {placeholders && placeholders.length > 0 ? (
              <div className="space-y-6">
                {/* Loop through each group */}
                {placeholders.map((group) => (
                  <div key={group.group}>
                    {/* ================================================== */}
                    {/* Group Header - Category Title */}
                    {/* ================================================== */}
                    <h3 className="text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wider">
                      {group.group}
                    </h3>

                    {/* ================================================== */}
                    {/* Group Items - Placeholder Buttons */}
                    {/* ================================================== */}
                    <div className="flex flex-wrap gap-2">
                      {group.items && group.items.length > 0 ? (
                        group.items.map((item) => (
                          <Button
                            key={item.value}
                            variant="outline"
                            onClick={() => handlePlaceholderClick(item.value)}
                          >
                            {item.label}
                          </Button>
                        ))
                      ) : (
                        <div className="text-sm text-gray-500">
                          No items in this group
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* No Placeholders Available */
              <div className="text-center py-8 text-gray-500">
                <p className="text-sm">No placeholders available</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PlaceholderModal;
